import React, { useState, useEffect } from 'react';
import {
  GripVerticalIcon, PenIcon, PlusIcon,
  XmarkIcon, CheckIcon
} from '@revfluence/fresh-icons/regular/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { useCustomStages } from '../context/CustomStagesContext';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'multiSelect' | 'yesNo' | 'checkbox' | 'date' | 'file' | 'url' | 'callToAction';
  isMandatory?: boolean;
  fieldOrder?: number;
  options?: string[];
  description?: string;
  campaignOnly?: boolean;
  isMemberLevel?: boolean;
  isProgramLevel?: boolean;
}

interface Props {
  fields: FormField[];
  onFieldsChange: (fields: FormField[]) => void;
}

const FormFieldsSectionComponent = ({ fields, onFieldsChange }: Props) => {
  const [showAddField, setShowAddField] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('Free Text');
  const [isMandatory, setIsMandatory] = useState(false);
  const [checkboxDescription, setCheckboxDescription] = useState('');
  const [options, setOptions] = useState<string[]>(['']);
  const [showOptions, setShowOptions] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  const { setHasUnsavedChanges } = useCustomStages();

  const isSelectType = fieldType === 'Single Select' || fieldType === 'Multi Select';

  // Reset form state when closing
  const resetForm = () => {
    setFieldName('');
    setFieldType('Free Text');
    setIsMandatory(false);
    setCheckboxDescription('');
    setOptions(['']);
    setShowOptions(false);
    setShowDescription(false);
  };

  // Load field data when editing
  useEffect(() => {
    if (editingFieldId) {
      const field = fields.find((f) => f.id === editingFieldId);
      if (field) {
        setFieldName(field.label);
        setFieldType(getFieldTypeDisplay(field.type));
        setIsMandatory(field.isMandatory || false);
        setCheckboxDescription(field.description || '');
        setOptions(field.options || ['']);
        setShowOptions(field.type === 'select' || field.type === 'multiSelect');
        setShowDescription(field.type === 'checkbox');
      }
    }
  }, [editingFieldId, fields]);

  const handleAddField = () => {
    setShowAddField(true);
    resetForm();
  };

  const handleEditField = (fieldId: string) => {
    setEditingFieldId(fieldId);
    setShowAddField(true);
  };

  const handleDeleteField = (fieldId: string) => {
    onFieldsChange(fields.filter((f) => f.id !== fieldId));
    setHasUnsavedChanges(true);
  };

  const getInternalFieldType = (displayType: string): FormField['type'] => {
    switch (displayType) {
      case 'Free Text':
        return 'text';
      case 'Number':
        return 'number';
      case 'Single Select':
        return 'select';
      case 'Multi Select':
        return 'multiSelect';
      case 'Yes/No':
        return 'yesNo';
      case 'Checkbox':
        return 'checkbox';
      case 'Date':
        return 'date';
      case 'Upload File':
        return 'file';
      case 'URL':
        return 'url';
      case 'Call to Action':
        return 'callToAction';
      default:
        return 'text';
    }
  };

  const getFieldTypeDisplay = (type: FormField['type']): string => {
    switch (type) {
      case 'text':
        return 'Free Text';
      case 'number':
        return 'Number';
      case 'select':
        return 'Single Select';
      case 'multiSelect':
        return 'Multi Select';
      case 'yesNo':
        return 'Yes/No';
      case 'checkbox':
        return 'Checkbox';
      case 'date':
        return 'Date';
      case 'file':
        return 'Upload File';
      case 'url':
        return 'URL';
      case 'callToAction':
        return 'Call to Action';
      default:
        return type;
    }
  };

  const handleSaveField = () => {
    // Basic validation
    if (!fieldName.trim()) {
      return; // Could add user feedback here
    }

    // For select types, require at least one valid option
    if (isSelectType) {
      const validOptions = options.filter((opt) => opt.trim() !== '');
      if (validOptions.length === 0) {
        return; // Could add user feedback here
      }
    }

    const newField = {
      id: editingFieldId || String(Date.now()),
      label: fieldName.trim(),
      type: getInternalFieldType(fieldType),
      isMandatory,
      fieldOrder: editingFieldId ? fields.find((f) => f.id === editingFieldId)?.fieldOrder || fields.length : fields.length,
      ...(fieldType === 'Checkbox' && { description: checkboxDescription }),
      ...(isSelectType && { options: options.filter((opt) => opt.trim() !== '') }),
    };

    if (editingFieldId) {
      onFieldsChange(fields.map((f) => {
        return f.id === editingFieldId ? newField : f;
      }));
    } else {
      onFieldsChange([...fields, newField]);
    }

    setHasUnsavedChanges(true);
    setShowAddField(false);
    setEditingFieldId(null);
    resetForm();
  };

  const handleFieldTypeChange = (type: string) => {
    setFieldType(type);
    setShowOptions(type === 'Single Select' || type === 'Multi Select');
    setShowDescription(type === 'Checkbox');
    if (type !== 'Checkbox') {
      setCheckboxDescription('');
    }
    if (type !== 'Single Select' && type !== 'Multi Select') {
      setOptions(['']);
    }
  };

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    try {
      const items = Array.from(fields);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update fieldOrder for all items to reflect new order
      const updatedItems = items.map((item, index) => ({
        ...item,
        fieldOrder: index
      }));

      onFieldsChange(updatedItems);
    } catch (error) {
      // Handle any errors during reordering gracefully
      console.warn('Error during field reordering:', error);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="self-stretch text-gray-500 text-sm font-normal font-['Inter'] leading-normal">
            Add any form fields to collect and track information.
          </div>
          <button
            className="h-6 flex items-center gap-2 text-sm font-medium text-gray-800"
            onClick={handleAddField}
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add Field
          </button>
        </div>
      </div>

      {showAddField && !editingFieldId && (
        <div className="self-stretch p-3 bg-gray-50 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 flex flex-col gap-3">
          {/* Add field form content */}
          <div className="flex-1 flex flex-col gap-3">
            {/* Basic Field Info */}
            <div className="self-stretch flex items-center gap-2">
              <Select value={fieldType} onValueChange={handleFieldTypeChange}>
                <SelectTrigger className="w-48 h-8 bg-white">
                  <SelectValue placeholder="Select Field Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Free Text">Free Text</SelectItem>
                  <SelectItem value="Number">Number</SelectItem>
                  <SelectItem value="Yes/No">Yes/No</SelectItem>
                  <SelectItem value="Checkbox">Checkbox</SelectItem>
                  <SelectItem value="Date">Date</SelectItem>
                  <SelectItem value="Single Select">Single Select</SelectItem>
                  <SelectItem value="Multi Select">Multi Select</SelectItem>
                  <SelectItem value="Upload File">Upload File</SelectItem>
                  {/* <SelectItem value="URL">URL</SelectItem>
                  <SelectItem value="Call to Action">Call to Action</SelectItem> */}
                </SelectContent>
              </Select>
              <Input
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                className="flex-1 h-8"
                placeholder="Enter field label"
              />
              <div className="flex justify-start items-start gap-2">
                <button
                  className={cn(
                    'w-8 h-8 px-2 py-1 rounded-lg flex justify-center items-center',
                    (fieldName.trim() && (!isSelectType || options.filter((opt) => opt.trim() !== '').length > 0)) ? 'bg-grey-6' : 'opacity-50 bg-grey-6'
                  )}
                  onClick={handleSaveField}
                  disabled={!fieldName.trim() || (isSelectType && options.filter((opt) => opt.trim() !== '').length === 0)}
                >
                  <CheckIcon className="w-3.5 h-3.5 text-white" />
                </button>
                <button
                  className="w-8 h-8 px-2 py-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 flex justify-center items-center"
                  onClick={() => {
                    setShowAddField(false);
                    setEditingFieldId(null);
                    resetForm();
                  }}
                >
                  <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
                </button>
              </div>
            </div>

            {/* Field Options */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="mandatory"
                  checked={isMandatory}
                  onCheckedChange={(checked) => setIsMandatory(checked as boolean)}
                />
                <label
                  htmlFor="mandatory"
                  className="text-gray-800 text-sm font-normal leading-normal"
                >
                  Mark as Mandatory
                </label>
              </div>
            </div>

            {/* Checkbox Description */}
            {showDescription && (
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-800">Checkbox Description</span>
                <Input
                  value={checkboxDescription}
                  onChange={(e) => setCheckboxDescription(e.target.value)}
                  className="h-8"
                  placeholder="Text appears next to checkbox"
                />
              </div>
            )}

            {/* Select Options */}
            {showOptions && (
              <div className="flex flex-col gap-2">
                {options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="h-8"
                    />
                    {options.length > 1 && (
                      <button
                        onClick={() => handleRemoveOption(index)}
                        className="w-8 h-8 px-2 py-1 opacity-50 rounded-lg inline-flex justify-center items-center gap-2"
                      >
                        <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={handleAddOption}
                  className="opacity-50 rounded-lg inline-flex justify-start items-center gap-2"
                >
                  <div className="h-3 flex justify-center items-center">
                    <PlusIcon className="w-3 text-gray-800" />
                  </div>
                  <div className="flex justify-center items-center gap-2.5">
                    <span className="text-gray-800 text-xs font-medium font-['Inter'] leading-tight">Add Option</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {fields.length === 0 && !showAddField ? (
        <div className="self-stretch inline-flex justify-center items-center">
          <div className="w-full p-2 bg-white flex justify-center items-start gap-2.5">
            <div className="flex-1 text-center text-gray-500 text-sm font-normal font-['Inter'] leading-normal">
              No fields added. Add fields to capture and record relevant information in this custom stage.
            </div>
          </div>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="form-fields">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="flex flex-col gap-2"
              >
                {fields.map((field, index) => (
                  <Draggable
                    key={field.id}
                    draggableId={field.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        {editingFieldId === field.id ? (
                          <div className="p-3 bg-gray-50 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 flex flex-col gap-3">
                            <div className="flex-1 flex flex-col gap-3">
                              {/* Basic Field Info */}
                              <div className="self-stretch flex items-center gap-2">
                                <Select value={fieldType} onValueChange={handleFieldTypeChange}>
                                  <SelectTrigger className="w-48 h-8 bg-white">
                                    <SelectValue placeholder="Select Field Type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Free Text">Free Text</SelectItem>
                                    <SelectItem value="Number">Number</SelectItem>
                                    <SelectItem value="Yes/No">Yes/No</SelectItem>
                                    <SelectItem value="Checkbox">Checkbox</SelectItem>
                                    <SelectItem value="Date">Date</SelectItem>
                                    <SelectItem value="Single Select">Single Select</SelectItem>
                                    <SelectItem value="Multi Select">Multi Select</SelectItem>
                                    <SelectItem value="Upload File">Upload File</SelectItem>
                                    {/* <SelectItem value="URL">URL</SelectItem>
                                    <SelectItem value="Call to Action">Call to Action</SelectItem> */}
                                  </SelectContent>
                                </Select>
                                <Input
                                  value={fieldName}
                                  onChange={(e) => setFieldName(e.target.value)}
                                  className="flex-1 h-8"
                                  placeholder="Enter field label"
                                />
                                <div className="flex justify-start items-start gap-2">
                                  <button
                                    className={cn(
                                      'w-8 h-8 px-2 py-1 rounded-lg flex justify-center items-center',
                                      (fieldName.trim() && (!isSelectType || options.filter((opt) => opt.trim() !== '').length > 0)) ? 'bg-grey-6' : 'opacity-50 bg-grey-6'
                                    )}
                                    onClick={handleSaveField}
                                    disabled={!fieldName.trim() || (isSelectType && options.filter((opt) => opt.trim() !== '').length === 0)}
                                  >
                                    <CheckIcon className="w-3.5 h-3.5 text-white" />
                                  </button>
                                  <button
                                    className="w-8 h-8 px-2 py-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 flex justify-center items-center"
                                    onClick={() => {
                                      setShowAddField(false);
                                      setEditingFieldId(null);
                                      resetForm();
                                    }}
                                  >
                                    <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
                                  </button>
                                </div>
                              </div>

                              {/* Field Options */}
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    id="mandatory"
                                    checked={isMandatory}
                                    onCheckedChange={(checked) => setIsMandatory(checked as boolean)}
                                  />
                                  <label
                                    htmlFor="mandatory"
                                    className="text-gray-800 text-sm font-normal leading-normal"
                                  >
                                    Mark as Mandatory
                                  </label>
                                </div>
                              </div>

                              {/* Checkbox Description */}
                              {showDescription && (
                                <div className="flex flex-col gap-2">
                                  <span className="text-xs font-medium text-gray-800">Checkbox Description</span>
                                  <Input
                                    value={checkboxDescription}
                                    onChange={(e) => setCheckboxDescription(e.target.value)}
                                    className="h-8"
                                    placeholder="Text appears next to checkbox"
                                  />
                                </div>
                              )}

                              {/* Select Options */}
                              {showOptions && (
                                <div className="flex flex-col gap-2">
                                  {options.map((option, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <Input
                                        value={option}
                                        onChange={(e) => handleOptionChange(index, e.target.value)}
                                        placeholder={`Option ${index + 1}`}
                                        className="h-8"
                                      />
                                      {options.length > 1 && (
                                        <button
                                          onClick={() => handleRemoveOption(index)}
                                          className="w-8 h-8 px-2 py-1 opacity-50 rounded-lg inline-flex justify-center items-center gap-2"
                                        >
                                          <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    onClick={handleAddOption}
                                    className="opacity-50 rounded-lg inline-flex justify-start items-center gap-2"
                                  >
                                    <div className="h-3 flex justify-center items-center">
                                      <PlusIcon className="w-3 text-gray-800" />
                                    </div>
                                    <div className="flex justify-center items-center gap-2.5">
                                      <span className="text-gray-800 text-xs font-medium font-['Inter'] leading-tight">Add Option</span>
                                    </div>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div
                            className={cn(
                              'flex items-center gap-2 p-2 border border-gray-200 rounded-xl',
                              snapshot.isDragging && 'bg-gray-50'
                            )}
                          >
                            <div
                              {...provided.dragHandleProps}
                              className="cursor-grab"
                            >
                              <GripVerticalIcon className="w-3 h-3 text-gray-300" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-gray-800">
                                  {field.label}
                                </span>
                                {field.isMandatory && (
                                  <span className="text-sm text-red-500">*</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                                onClick={() => handleEditField(field.id)}
                              >
                                <PenIcon className="w-3.5 h-3.5 text-gray-800" />
                              </button>
                              <button
                                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                                onClick={() => handleDeleteField(field.id)}
                              >
                                <XmarkIcon className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

FormFieldsSectionComponent.displayName = 'FormFieldsSection';

export const FormFieldsSection = React.memo(FormFieldsSectionComponent);
